import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { OFFICES, PHONES, FIRM_EMAIL } from "../data/firmContent";
import useSEO from "../lib/useSEO";

export default function Contact() {
    useSEO({ title: "Contact & Consultation", description: "Speak with counsel. Head Office: 50 Queen St, Yaba, Lagos. Abuja Office: Plot 943, Mabushi. Phone 0911 083 4432.", path: "/contact" });
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Your inquiry has been received. A counsel will respond shortly.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send message");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="page-contact">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Contact &amp; Consultation</div>
                <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-tight">
                    Speak with counsel.
                </h1>
                <p className="mt-6 max-w-2xl text-slate-800 leading-relaxed text-[17px]">
                    All inquiries are reviewed by a senior associate before assignment to the appropriate partner.
                    Communications are held in strict confidence.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
                <div className="md:col-span-5 space-y-10" data-testid="contact-info">
                    {[OFFICES.head, OFFICES.abuja].map((o) => (
                        <div key={o.label} className="border border-slate-900/10 bg-[#faf9f6] p-7">
                            <div className="font-mono-cap text-[10px] text-[#c5a059] mb-3 flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> {o.label}
                            </div>
                            <address className="not-italic text-base leading-relaxed text-slate-700 font-serif-display">
                                {o.lines.map((l) => <div key={l}>{l}</div>)}
                            </address>
                        </div>
                    ))}
                    <div className="border border-slate-900/10 bg-[#faf9f6] p-7">
                        <div className="font-mono-cap text-[10px] text-[#c5a059] mb-3 flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5" /> Telephone
                        </div>
                        <ul className="space-y-1 text-base text-slate-700">
                            {PHONES.map((p) => (
                                <li key={p}><a className="rj-link" href={`tel:${p.replace(/\s/g, "")}`}>{p}</a></li>
                            ))}
                        </ul>
                        <div className="mt-5 font-mono-cap text-[10px] text-[#c5a059] mb-2 flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5" /> Email
                        </div>
                        <a className="rj-link text-base text-slate-700" href={`mailto:${FIRM_EMAIL}`}>{FIRM_EMAIL}</a>
                    </div>
                </div>

                <form onSubmit={submit} className="md:col-span-7 border border-slate-900/10 bg-[#faf9f6] p-7 lg:p-10 space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                        <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
                        <Field label="Subject" required value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
                    </div>
                    <Field label="How can we help?" textarea required value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="contact-message" />
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="contact-submit"
                        className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-7 py-3.5 rounded-sm font-medium hover:bg-[#c5a059] hover:text-[#0F172A] transition-colors disabled:opacity-50"
                    >
                        <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send inquiry"}
                    </button>
                </form>
            </section>

            {/* MAP */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="contact-map">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Locate the Chambers</div>
                <h2 className="font-serif-display text-3xl md:text-4xl mb-6">50 Queen Street · Lagos.</h2>
                <div className="border border-slate-900/10 overflow-hidden">
                    <iframe
                        title="RJ & Co Lagos Office Map"
                        src="https://www.google.com/maps?q=50%20Queen%20St%2C%20Lagos%20Mainland%2C%20Lagos%20101245%2C%20Nigeria&output=embed"
                        width="100%"
                        height="460"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </section>
        </div>
    );
}

const Field = ({ label, value, onChange, required, type = "text", textarea, testid }) => (
    <label className="block">
        <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">{label}{required && " *"}</span>
        {textarea ? (
            <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} rows={5} data-testid={testid}
                className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]" />
        ) : (
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
                className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00a3e0]" />
        )}
    </label>
);
